import type { ChatIdentityKind, ThreadRecipient } from '@/api/chat/types';
import type { BroadcastSubject, MemberKind } from '@/api/chat/v2-types';

import type { TopicColor } from './components';

export interface TopicRef {
  id: string;
  label: string;
  color: TopicColor;
  locked?: boolean;
}

export interface TopicRecord extends TopicRef {
  archivedAt: string | null;
  isSystem?: boolean;
  visibleToRoleIds?: string[];
}

export interface ParticipantRef {
  id: string;
  name: string;
  subtitle?: string;
  avatarUrl?: string | null;
  kind: 'contact' | 'org';
  identityKind?: ChatIdentityKind;
}

export interface QuotedAttachment {
  filename: string;
  count: number;
}

export interface QuotedMessage {
  messageId?: string;
  author: string;
  excerpt: string;
  deleted?: boolean;
  attachment?: QuotedAttachment;
}

export interface MessageAttachment {
  fileId: string;
  filename: string;
  size: number;
  contentType: string;
  url?: string;
}

export interface SendMessageOptions {
  replyToMessageId?: string;
  forwardOfMessageId?: string;
  attachmentIds?: string[];
}

export interface ComposerReference {
  kind: 'quote' | 'forward';
  message: ThreadMessageView;
}

export interface ConversationMessage {
  kind: 'message';
  id: string;
  threadId: string;
  threadName: string;
  topics: TopicRef[];
  sender: ParticipantRef;
  originalSenderId?: string;
  body: string | null;
  sentAt: string;
  isOwn: boolean;
  isUnread: boolean;
  replyTo?: QuotedMessage;
  forwardedFrom?: QuotedMessage;
  attachmentCount?: number;
  attachments?: MessageAttachment[];
  isBroadcast?: boolean;
  /** Deep link of a broadcast entry to the object it announces. */
  link?: string | null;
}

export interface FeedEntrySummary {
  id: string;
  body: string | null;
}

export interface ConversationSystemEvent {
  kind: 'system';
  id: string;
  threadId: string;
  threadName: string;
  text: string;
  sentAt: string;
}

export type ConversationEntry = ConversationMessage | ConversationSystemEvent;

export const CONTEXT_KINDS = ['contact', 'topic', 'broadcast', 'groupChat'] as const;
export type ContextKind = (typeof CONTEXT_KINDS)[number];

export const KIND_FILTERS = ['all', ...CONTEXT_KINDS] as const;
export type KindFilter = (typeof KIND_FILTERS)[number];

export const RIGHT_PANE_MODES = ['expanded', 'rail'] as const;
export type RightPaneMode = (typeof RIGHT_PANE_MODES)[number];

export const SEND_AS_KINDS = ['individual', 'gammaDesk', 'entityDesk'] as const;
export type SendAsKind = (typeof SEND_AS_KINDS)[number];

export interface ThreadOverride {
  name?: string;
  preview?: string;
  lastActivityAt?: string;
  unreadCount?: number;
  hasMention?: boolean;
  isMuted?: boolean;
  isWaiting?: boolean;
  isPrivate?: boolean;
  isResolved?: boolean;
  isArchived?: boolean;
}

export type ThreadOverrides = Record<string, ThreadOverride>;

export type ThreadFilings = Record<string, string[]>;

export interface SendAsIdentity {
  kind: SendAsKind;
  participantId?: string;
  label: string;
  isDesk: boolean;
}

export type ResolvedSendAsIdentity = SendAsIdentity & { participantId: string };

/** Mirrors the `state` values the context directory accepts, including the ones no longer offered. */
export const STATE_FILTERS = [
  'needsAction',
  'unread',
  'awaitingReply',
  'waiting',
  'muted',
] as const;
export type StateFilter = (typeof STATE_FILTERS)[number];

/**
 * `needsAction` and `waiting` are deliberately absent: both restated what the row already shows,
 * and neither survived review. The API still answers for them, so they stay in `STATE_FILTERS`.
 */
export const VISIBLE_STATE_FILTERS = ['unread', 'awaitingReply'] as const;
export const OVERFLOW_STATE_FILTERS = ['muted'] as const;

export interface ThreadSummary {
  id: string;
  name: string;
  topics: TopicRef[];
  preview: string;
  lastActivityAt: string;
  unreadCount: number;
  hasMention: boolean;
  awaitingMyReply?: boolean;
  isWaiting: boolean;
  isMuted: boolean;
  isPrivate: boolean;
  isBroadcast: boolean;
  isOwnBroadcast?: boolean;
  /** The Fund, Company or Event this thread follows; `null` for every conversation. */
  subject?: BroadcastSubject | null;
  ownIdentity: SendAsKind;
  attachmentCount?: number;
  filedContextIds?: string[];
  parentThreadId?: string | null;
  isResolved?: boolean;
  isArchived?: boolean;
}

export type ThreadAction =
  | 'rename'
  | 'add-people'
  | 'participants'
  | 'private'
  | 'waiting'
  | 'mute'
  | 'resolve'
  | 'archive';

export interface ContextItem {
  id: string;
  kind: ContextKind;
  name: string;
  subtitle?: string;
  avatarUrl?: string | null;
  color?: TopicColor;
  locked?: boolean;
  threads: ThreadSummary[];
  lastActivityAt: string | null;
  /** Counts come from the context directory and can exceed the loaded thread page. */
  threadCount?: number;
  unreadCount?: number;
  memberKinds?: MemberKind[];
}

export interface ThreadMessageView {
  id: string;
  sender: ParticipantRef;
  originalSenderId?: string;
  isBroadcast?: boolean;
  body: string | null;
  sentAt: string;
  isOwn: boolean;
  isUnread?: boolean;
  replyTo?: QuotedMessage;
  forwardedFrom?: QuotedMessage;
  attachmentCount?: number;
  attachments?: MessageAttachment[];
  isSystem?: boolean;
  link?: string | null;
}

export interface ContactDetails {
  id: string;
  name: string;
  subtitle?: string;
  avatarUrl?: string | null;
  kind: 'contact' | 'org' | 'entity';
  relationships: string[];
  coInvestorNote?: string;
}

export interface NewMessageRecipientSeed {
  recipient: ThreadRecipient;
  name: string;
}
